/**
 * Turns a /api/validate-domain response into the view model for the domain
 * test results panel. All user-facing wording for the checks lives here, not
 * in the rendering component.
 *
 * @typedef {import('../../functions/validate-domain').ValidateDomainClientResponse} ValidateDomainClientResponse
 * @typedef {import('../../functions/validate-domain').DomainValidityReport} DomainValidityReport
 */

export const DOMAIN_CHECK_STATUS = {
    PASS: 'pass',
    WARN: 'warn',
    FAIL: 'fail',
};

// The CAA records Ghost(Pro)'s certificate providers need. Mirrors Daisy's
// supported issuers (certainly.com primary, letsencrypt.org fallback).
const CAA_FIX =
    'Add CAA records allowing our certificate providers — 0 issue "certainly.com" and ' +
    '0 issue "letsencrypt.org" — or remove the CAA records entirely, then test again.';

// Daisy warning code for a missing root <-> www redirect record.
const NO_REDIRECT_RECORD_WARNING = 'DNS_WARN_NO_REDIRECT_RECORD';

/**
 * @typedef {object} DomainCheck
 * @property {string} key - stable identifier ('dns', 'caa')
 * @property {string} status - one of DOMAIN_CHECK_STATUS
 * @property {string} title - the finding, stated as the row's headline
 * @property {string} [detail] - extra context beyond the headline
 * @property {string} [fix] - how to resolve it (fail/warn only)
 */

/**
 * The title states the finding for the tested domain, so the icon and the
 * headline always agree; a detail line is only added when there's more to say
 * than the headline itself.
 * @param {string} domain
 * @param {DomainValidityReport} report
 * @returns {DomainCheck}
 */
const buildDnsCheck = (domain, report) => {
    const check = { key: 'dns' };

    if (report.isProxy) {
        if (report.isProxyValid) {
            return {
                ...check,
                status: DOMAIN_CHECK_STATUS.PASS,
                title: `${domain} is served through a proxy that's correctly configured for Ghost(Pro)`,
            };
        }

        return {
            ...check,
            status: DOMAIN_CHECK_STATUS.FAIL,
            title: `${domain} is behind a proxy that isn't passing traffic to Ghost(Pro) correctly`,
            fix: 'Check your proxy configuration, or remove the proxy and point the domain directly at the records shown above.',
        };
    }

    switch (report.dnsRating) {
        case 'correct':
            return {
                ...check,
                status: DOMAIN_CHECK_STATUS.PASS,
                title: `${domain} points to your publication`,
            };
        case 'missing':
            return {
                ...check,
                status: DOMAIN_CHECK_STATUS.FAIL,
                title: `We couldn't find any records for ${domain} yet`,
                fix: 'Add the records shown above at your registrar. DNS changes can take a few minutes to appear, so if you just added them, test again shortly.',
            };
        case 'mismatch':
            return {
                ...check,
                status: DOMAIN_CHECK_STATUS.FAIL,
                title: `The CNAME record for ${domain} points at a different Ghost(Pro) site`,
                fix: 'Update the CNAME record at your registrar to the value shown above.',
            };
        case 'incorrect':
            return {
                ...check,
                status: DOMAIN_CHECK_STATUS.FAIL,
                title: `Found existing records for ${domain} that point somewhere else`,
                fix: 'Remove the existing records for this name at your registrar, then add the records shown above.',
            };
        case 'obfuscated':
            if (report.isGhostPro) {
                return {
                    ...check,
                    status: DOMAIN_CHECK_STATUS.PASS,
                    title: `${domain} resolves to your publication through your DNS provider's proxy`,
                };
            }

            return {
                ...check,
                status: DOMAIN_CHECK_STATUS.WARN,
                title: `We couldn't fully verify where ${domain} points`,
                detail: 'Your DNS provider hides the underlying records.',
                fix: 'Double-check your records match the table above, then test again.',
            };
        default:
            return {
                ...check,
                status: DOMAIN_CHECK_STATUS.WARN,
                title: `We couldn't verify where ${domain} points`,
                fix: 'Double-check your records match the table above, then test again.',
            };
    }
};

/**
 * The CAA check only surfaces problems: a valid (or absent) CAA record isn't
 * worth a positive note, so this returns null when nothing blocks SSL.
 * @param {string} domain
 * @param {DomainValidityReport} report
 * @returns {DomainCheck|null}
 */
const buildCaaCheck = (domain, report) => {
    if (report.caaError) {
        return {
            key: 'caa',
            title: "CAA records couldn't be checked",
            status: DOMAIN_CHECK_STATUS.WARN,
            detail: `We couldn't check the CAA records for ${domain}.`,
            fix: 'Test again in a few minutes.',
        };
    }

    if (report.isCaaValid === false) {
        const source = report.caaSource || domain;
        const allowed = report.caaAllowedIssuers?.length
            ? ` (currently allowed: ${report.caaAllowedIssuers.join(', ')})`
            : '';

        return {
            key: 'caa',
            title: 'Invalid CAA record',
            status: DOMAIN_CHECK_STATUS.FAIL,
            detail: `A CAA record on ${source} only allows other certificate authorities to issue certificates${allowed}, so we can't generate an SSL certificate for ${domain}.`,
            fix: CAA_FIX,
        };
    }

    return null;
};

/**
 * The redirect record (root <-> www) is non-essential — a domain activates
 * fine without it — so Daisy reports its absence as a warning rather than a
 * failure, and this check never blocks anything.
 * @param {string} domain
 * @param {DomainValidityReport} report
 * @param {Array<{code?: string, message?: string}>} [warnings]
 * @returns {DomainCheck|null}
 */
const buildRedirectCheck = (domain, report, warnings) => {
    const isMissingRedirectRecord = (warnings || []).some(
        (warning) => warning?.code === NO_REDIRECT_RECORD_WARNING,
    );

    if (!isMissingRedirectRecord) {
        return null;
    }

    const redirectDomain = report?.redirectDomain;

    return {
        key: 'redirect',
        status: DOMAIN_CHECK_STATUS.WARN,
        title: redirectDomain
            ? `No redirect record found for ${redirectDomain}`
            : 'No redirect record found',
        detail: redirectDomain
            ? `Visitors to ${redirectDomain} won't be redirected to ${domain}. This is optional — you can still activate your domain without it.`
            : 'This is optional — you can still activate your domain without it.',
        fix: 'To set up the redirect, add the A record shown above at your registrar, then test again.',
    };
};

/**
 * @param {ValidateDomainClientResponse} response
 * @returns {{allPassed: boolean, checks: DomainCheck[]}|null}
 */
export const buildDomainTestResults = (response) => {
    const report = response?.validity_report;

    if (!report) {
        return null;
    }

    const domain = response.domain || '';
    const isValid = response.is_valid === true;

    const checks = [buildDnsCheck(domain, report)];

    const caaCheck = buildCaaCheck(domain, report);
    if (caaCheck) {
        checks.push(caaCheck);
    }

    const redirectCheck = buildRedirectCheck(domain, report, response.warnings);
    if (redirectCheck) {
        checks.push(redirectCheck);
    }

    // Daisy's overall verdict wins: if it reports the domain as invalid but no
    // individual check failed (e.g. a report shape this mapper doesn't know
    // yet), escalate the DNS check so the result never overpromises.
    const hasFailure = checks.some((check) => check.status === DOMAIN_CHECK_STATUS.FAIL);
    if (!isValid && !hasFailure) {
        checks[0] = {
            ...checks[0],
            status: DOMAIN_CHECK_STATUS.FAIL,
            title: `${domain} isn't ready to activate yet`,
            fix:
                checks[0].fix ||
                'Double-check your records match the table above, then test again.',
        };
    }

    const allPassed = isValid && checks.every((check) => check.status === DOMAIN_CHECK_STATUS.PASS);

    return {
        allPassed,
        checks,
    };
};
